import { useState } from "react";

import { type Zone } from "@/lib/building-domain";
import { useEquipmentTypeMeta } from "@/lib/equipment-type-meta";
import { inputGlowWrap, inputGlowInner } from "@/lib/card-styles";
import { EquipmentTypeSelect } from "./EquipmentTypeSelect";

// ── EquipmentForm ─────────────────────────────────────────────────────────────

interface FormData {
  name: string;
  equipment_type: string;
  equipment_tag: string;
  zone_ids: string[];
  location: string | null;
}

interface FormProps {
  zones: Zone[];
  locations: string[];
  on_submit: (data: FormData) => Promise<void>;
  on_cancel: () => void;
}

export function EquipmentForm({
  zones,
  locations,
  on_submit,
  on_cancel,
}: FormProps) {
  const eqMeta = useEquipmentTypeMeta();
  const [name, set_name] = useState("");
  const [equipment_type, set_equipment_type] =
    useState<string>("air-handling-unit");
  const [tag, set_tag] = useState("");
  const [zone_ids, set_zone_ids] = useState<string[]>([]);
  const [location, set_location] = useState<string>("");
  const [submitting, set_submitting] = useState(false);
  const [submit_error, set_submit_error] = useState<string | null>(null);

  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    set_submitting(true);
    set_submit_error(null);
    try {
      await on_submit({
        name: name.trim(),
        equipment_type,
        equipment_tag: tag.trim(),
        zone_ids,
        location: location || null,
      });
    } catch (err) {
      set_submit_error(
        err instanceof Error
          ? err.message
          : "Tallennus epäonnistui. Yritä uudelleen.",
      );
    } finally {
      set_submitting(false);
    }
  }

  return (
    <form
      onSubmit={(e) => void handle_submit(e)}
      className="rounded-lg border border-primary/40 bg-card p-4"
    >
      <p className="mb-3 text-sm font-medium">Uusi talotekniikka</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">
            Nimi
          </label>
          <div className={inputGlowWrap}>
            <div className={inputGlowInner}>
              <input
                autoFocus
                value={name}
                onChange={(e) => set_name(e.target.value)}
                placeholder="esim. IV-kone 1"
                className="w-full bg-transparent px-2 py-1.5 text-sm placeholder:text-muted-foreground outline-none"
              />
            </div>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">
            Tunniste
          </label>
          <div className={inputGlowWrap}>
            <div className={inputGlowInner}>
              <input
                value={tag}
                onChange={(e) => set_tag(e.target.value)}
                placeholder="esim. IV01"
                className="w-full bg-transparent px-2 py-1.5 text-sm placeholder:text-muted-foreground outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3">
        <label className="mb-1 block text-xs text-muted-foreground">
          Tyyppi
        </label>
        <EquipmentTypeSelect
          eqMeta={eqMeta}
          value={equipment_type}
          onChange={set_equipment_type}
        />
      </div>

      {(locations.length > 0 || zones.length > 0) && (
        <div className="mt-3 grid grid-cols-2 gap-3">
          {locations.length > 0 && (
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">
                Sijainti
              </label>
              <select
                value={location}
                onChange={(e) => set_location(e.target.value)}
                className="w-full rounded border border-border/40 bg-background px-2 py-1.5 text-sm outline-none focus:border-primary/60"
              >
                <option value="">—</option>
                {locations.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </select>
            </div>
          )}
          {zones.length > 0 && (
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">
                Palvelee vyöhykkeitä
              </label>
              <div className="rounded border border-border/40 bg-background px-2 py-1.5 space-y-1 max-h-28 overflow-y-auto">
                {zones.map((z) => (
                  <label
                    key={z.id}
                    className="flex items-center gap-2 text-xs cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={zone_ids.includes(z.id)}
                      onChange={(e) => {
                        set_zone_ids((prev) =>
                          e.target.checked
                            ? [...prev, z.id]
                            : prev.filter((id) => id !== z.id),
                        );
                      }}
                      className="rounded"
                    />
                    {z.name}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={submitting || !name.trim()}
          className="rounded bg-primary px-3 py-1.5 text-xs text-primary-foreground disabled:opacity-50"
        >
          {submitting ? "Tallennetaan..." : "Lisää"}
        </button>
        <button
          type="button"
          onClick={on_cancel}
          className="rounded px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          Peruuta
        </button>
      </div>
      {submit_error && (
        <p className="mt-2 text-xs text-destructive">{submit_error}</p>
      )}
    </form>
  );
}
